<template>
    <o-notification
        :variant="props.variant"
        :closable="props.closable"
        :class="[props.variantClass]"
        @close="emit('close')"
    >
        <div class="flex gap-3">
            <UiIcon
                v-if="icon"
                :size="24"
                :icon="icon"
                :class="{
                    'text-[#1773C2]': props.variant === NotificationVariant.Info,
                    'text-[#348B47]': props.variant === NotificationVariant.Success,
                    'text-[#E5A646]': props.variant === NotificationVariant.Warning,
                    'text-[#D04F47]': props.variant === NotificationVariant.Danger,
                }"
                class="flex-none"
            />
            <div
                class="flex flex-col items-start"
                :class="{
                    'mr-5': props.closable,
                }"
            >
                <p
                    v-if="props.title"
                    class="font-bold mb-1 text-base"
                >
                    {{ props.title }}
                </p>
                <p
                    v-if="props.message"
                    :class="{
                        [props.messageFontSizeClass]: true,
                        'mb-4': !!$slots.default,
                    }"
                    v-html="props.message"
                />
                <slot v-if="!!$slots.default" />
            </div>
        </div>
    </o-notification>
</template>

<script lang="ts" setup>
    import { NotificationVariant } from '@/enums/ui.enum';

    const props = withDefaults(
        defineProps<{
            icon?: string | boolean;
            title?: string;
            variant?: `${NotificationVariant}`;
            message?: string;
            closable?: boolean;
            variantClass?: string;
            messageFontSizeClass?: string;
        }>(),
        {
            icon: false,
            title: '',
            variant: NotificationVariant.Info,
            closable: false,
            variantClass: '',
            messageFontSizeClass: 'text-base',
        },
    );

    const emit = defineEmits<{
        close: [];
    }>();

    const icon = computed(() => {
        if (props.icon === true) {
            switch (props.variant) {
                case NotificationVariant.Info:
                    return 'alert-solid';
                case NotificationVariant.Success:
                    return 'check-circle-solid';
                case NotificationVariant.Warning:
                    return 'exclamation-solid';
                case NotificationVariant.Danger:
                    return 'exclamation-triangle-solid';
            }
        }

        return props.icon;
    });
</script>

<style lang="scss" scoped>
    @use '@/assets/scss/imports';

    .notification {
        padding: 1rem;
        border-radius: calc(imports.$border-radius / 2);

        &.is-info {
            background-color: imports.$notify-info-color;
            color: imports.$color-gray-80;
        }

        &.is-success {
            background-color: imports.$notify-success-color;
            color: imports.$color-gray-80;
        }

        &.is-warning {
            background-color: imports.$notify-warning-color;
            color: imports.$color-gray-80;
        }

        &.is-danger {
            background-color: #fff3f2;
            color: imports.$color-gray-80;
        }
    }
</style>
